import { useCallback, useEffect, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import Barcode from '@/components/feature/Barcode';
import type { DiscountCode, DiscountScope } from '@/lib/types';

interface Cashier {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

interface FormState {
  code: string;
  percentage: number;
  description: string;
  starts_at: string;
  ends_at: string;
  usage_limit: string;
  active: boolean;
  authorized_cashier_id: string;
}

const emptyForm: FormState = {
  code: '',
  percentage: 0,
  description: '',
  starts_at: '',
  ends_at: '',
  usage_limit: '',
  active: true,
  authorized_cashier_id: '',
};

const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

export default function DiscountCodes({ scope }: { scope: DiscountScope }) {
  const [items, setItems] = useState<DiscountCode[]>([]);
  const [usage, setUsage] = useState<Record<string, number>>({});
  const [cashiers, setCashiers] = useState<Cashier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DiscountCode | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [barcodeCode, setBarcodeCode] = useState<DiscountCode | null>(null);

  const isCashier = scope === 'cashier';
  const prefix = isCashier ? 'CAJ' : 'ADMIN';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('discount_codes')
        .select('*, authorized_cashier:profiles!authorized_cashier_id(first_name, last_name)')
        .eq('scope', scope)
        .order('created_at', { ascending: false });
      if (e) throw e;
      setItems((data as DiscountCode[]) ?? []);

      const { data: usages } = await supabase.from('discount_code_usages').select('code_id');
      const map: Record<string, number> = {};
      (usages ?? []).forEach((u: { code_id: string }) => {
        map[u.code_id] = (map[u.code_id] ?? 0) + 1;
      });
      setUsage(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los códigos');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isCashier) return;
    const fetchCashiers = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .eq('role', 'cashier')
        .order('first_name');
      setCashiers((data as Cashier[]) ?? []);
    };
    fetchCashiers();
  }, [isCashier]);

  const suggestCode = () => {
    const year = new Date().getFullYear();
    const rand = Math.floor(100 + Math.random() * 900);
    setForm((p) => ({ ...p, code: `${prefix}-${year}-${rand}` }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (c: DiscountCode) => {
    setEditing(c);
    setForm({
      code: c.code,
      percentage: Number(c.percentage),
      description: c.description ?? '',
      starts_at: toDateInput(c.starts_at),
      ends_at: toDateInput(c.ends_at),
      usage_limit: c.usage_limit != null ? String(c.usage_limit) : '',
      active: c.active,
      authorized_cashier_id: c.authorized_cashier_id ?? '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const code = form.code.trim().toUpperCase();
    if (!code) {
      setError('El código es obligatorio.');
      return;
    }
    if (form.percentage < 0 || form.percentage > 100) {
      setError('El porcentaje debe estar entre 0 y 100.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        scope,
        code,
        percentage: form.percentage,
        description: form.description.trim() || null,
        starts_at: form.starts_at ? `${form.starts_at}T00:00:00` : null,
        ends_at: form.ends_at ? `${form.ends_at}T23:59:59` : null,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit, 10) : null,
        active: form.active,
        authorized_cashier_id: isCashier && form.authorized_cashier_id ? form.authorized_cashier_id : null,
      };
      if (editing) {
        const { error: e } = await supabase
          .from('discount_codes')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', editing.id);
        if (e) throw e;
      } else {
        const { error: e } = await supabase.from('discount_codes').insert(payload);
        if (e) throw e;
      }
      setModalOpen(false);
      load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo guardar el código';
      setError(msg.includes('duplicate') ? 'Ya existe un código con ese valor. Usa otro.' : msg);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c: DiscountCode) => {
    const { error: e } = await supabase
      .from('discount_codes')
      .update({ active: !c.active, updated_at: new Date().toISOString() })
      .eq('id', c.id);
    if (e) return;
    load();
  };

  const remove = async (c: DiscountCode) => {
    const { error: e } = await supabase.from('discount_codes').delete().eq('id', c.id);
    if (e) return;
    load();
  };

  const printBarcode = (c: DiscountCode) => {
    const img = document.createElement('img');
    JsBarcode(img, c.code, {
      format: 'CODE128',
      height: 60,
      displayValue: true,
      fontSize: 16,
      margin: 4,
      lineColor: '#000000',
      background: '#ffffff',
    });
    const w = window.open('', '_blank', 'width=420,height=320');
    if (!w) return;
    w.document.write(
      `<html><head><title>${c.code}</title></head><body style="font-family:sans-serif;padding:24px;text-align:center;">` +
        `<h2 style="margin:0 0 4px">Código de descuento</h2>` +
        `<p style="margin:0 0 16px;font-size:18px;font-weight:bold">${c.code}</p>` +
        `<div><img src="${img.src}" style="max-width:100%"/></div>` +
        `<p style="margin:16px 0 0;font-size:15px">Descuento: ${c.percentage}%</p>` +
        `<script>window.onload=function(){window.print()}<\/script>` +
        `</body></html>`
    );
    w.document.close();
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-600">
          {items.length} {items.length === 1 ? 'código' : 'códigos'}
          {isCashier ? ' para cajeros' : ' de administrador'}.
        </p>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-add-line" /> Nuevo código
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-coupon-3-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay códigos de este tipo.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-background-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Código</th>
                <th className="px-4 py-3 font-medium">Descuento</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Descripción</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Usos / Límite</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Vigencia</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-background-50">
                  <td className="px-4 py-3">
                    <span className="font-mono font-semibold text-foreground-950">{c.code}</span>
                  </td>
                  <td className="px-4 py-3 text-accent-700 font-semibold">-{Number(c.percentage)}%</td>
                  <td className="px-4 py-3 text-foreground-600 hidden md:table-cell">
                    {c.description ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground-600 hidden lg:table-cell">
                    {usage[c.id] ?? 0}
                    {c.usage_limit != null ? ` / ${c.usage_limit}` : ''}
                  </td>
                  <td className="px-4 py-3 text-foreground-500 hidden lg:table-cell">
                    {c.starts_at || c.ends_at
                      ? `${c.starts_at ? new Date(c.starts_at).toLocaleDateString('es-DO') : '—'} → ${
                          c.ends_at ? new Date(c.ends_at).toLocaleDateString('es-DO') : '—'
                        }`
                      : 'Sin límite'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.active ? 'bg-accent-100 text-accent-900' : 'bg-secondary-100 text-secondary-900'
                      }`}
                    >
                      {c.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setBarcodeCode(c)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                        title="Código de barras"
                      >
                        <i className="ri-barcode-line" />
                      </button>
                      <button
                        onClick={() => toggle(c)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                        title={c.active ? 'Desactivar' : 'Activar'}
                      >
                        <i className={c.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                      </button>
                      <button
                        onClick={() => openEdit(c)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => remove(c)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-accent-50 hover:text-accent-700 cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-6-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar código' : `Nuevo código de ${isCashier ? 'cajero' : 'administrador'}`}
        footer={
          <>
            <button
              onClick={() => setModalOpen(false)}
              disabled={saving}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Código *</label>
            <div className="flex gap-2">
              <input
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                placeholder={`${prefix}-2026-001`}
                className={inputClass}
              />
              {!editing && (
                <button
                  onClick={suggestCode}
                  className="shrink-0 inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                  title="Sugerir código"
                >
                  <i className="ri-refresh-line" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>Porcentaje de descuento (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.percentage}
              onChange={(e) => setForm((p) => ({ ...p, percentage: parseFloat(e.target.value) || 0 }))}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Descripción</label>
            <input
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Ej. Promoción de temporada"
              className={inputClass}
            />
          </div>

          {isCashier && (
            <div>
              <label className={labelClass}>Cajero autorizado (opcional)</label>
              <select
                value={form.authorized_cashier_id}
                onChange={(e) => setForm((p) => ({ ...p, authorized_cashier_id: e.target.value }))}
                className={inputClass}
              >
                <option value="">Todos los cajeros</option>
                {cashiers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {[c.first_name, c.last_name].filter(Boolean).join(' ') || 'Cajero'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Fecha de inicio</label>
              <input
                type="date"
                value={form.starts_at}
                onChange={(e) => setForm((p) => ({ ...p, starts_at: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Fecha de vencimiento</label>
              <input
                type="date"
                value={form.ends_at}
                onChange={(e) => setForm((p) => ({ ...p, ends_at: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Límite de usos (vacío = ilimitado)</label>
            <input
              type="number"
              min="0"
              value={form.usage_limit}
              onChange={(e) => setForm((p) => ({ ...p, usage_limit: e.target.value }))}
              placeholder="Sin límite"
              className={inputClass}
            />
          </div>

          <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
            <span className="text-sm font-medium text-foreground-900">Código activo</span>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
              className="w-5 h-5 accent-primary-500 cursor-pointer"
            />
          </label>

          {error && (
            <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
              {error}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={barcodeCode != null}
        onClose={() => setBarcodeCode(null)}
        title="Código de barras"
        footer={
          <>
            <button
              onClick={() => setBarcodeCode(null)}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cerrar
            </button>
            <button
              onClick={() => barcodeCode && printBarcode(barcodeCode)}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              <i className="ri-printer-line" /> Imprimir
            </button>
          </>
        }
      >
        {barcodeCode && (
          <div className="text-center">
            <p className="text-lg font-mono font-semibold text-foreground-950">{barcodeCode.code}</p>
            <p className="mt-1 text-sm text-foreground-600">Descuento: {Number(barcodeCode.percentage)}%</p>
            <div className="mt-4 flex justify-center">
              <Barcode value={barcodeCode.code} height={60} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}